import {
  CHAT_MAX_LENGTH,
  CLIENT_MESSAGES,
  type ChatChannel,
  type ChatSend,
  type TypingMessage,
} from '@veriverse/shared';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isTyping } from '../game/typing.js';
import { createTypingNotifier } from '../net/typing-indicator.js';
import { useSessionStore } from '../state/session-store.js';
import { formatChatTimestamp } from '../state/office-clock.js';

/** Global and nearby chat. Press Enter anywhere to start typing; Escape returns to the game. */
export function ChatPanel() {
  const { t } = useTranslation();
  const room = useSessionStore((s) => s.room);
  const log = useSessionStore((s) => s.chatLog);
  const notice = useSessionStore((s) => s.chatNotice);
  const [channel, setChannel] = useState<ChatChannel>('proximity');
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  // Nearby players see a "..." over our head while we type a nearby message (spec §18).
  const typing = useMemo(
    () =>
      createTypingNotifier((active) => {
        const message: TypingMessage = { typing: active };
        room?.send(CLIENT_MESSAGES.typing, message);
      }),
    [room],
  );
  useEffect(() => () => typing.stop(), [typing]);
  const nearbyText = (value: string, on: ChatChannel) => on === 'proximity' && value.trim() !== '';

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isTyping()) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [log.length]);

  const send = (event: FormEvent) => {
    event.preventDefault();
    const body = text.trim();
    if (!body || !room) return;
    const message: ChatSend = { channel, body };
    room.send(CLIENT_MESSAGES.chat, message);
    useSessionStore.getState().setChatNotice(undefined);
    typing.stop();
    setText('');
    inputRef.current?.blur(); // hand the keyboard back to the game; Enter focuses the chat again
  };

  return (
    <section className="chat hud-box" aria-label={t('chat.title')}>
      <ol ref={listRef} className="chat-log" aria-live="polite">
        {log.map((entry) => (
          <li key={entry.id} className={entry.channel === 'proximity' ? 'near' : undefined}>
            <time dateTime={new Date(entry.at).toISOString()}>
              {formatChatTimestamp(new Date(entry.at))}
            </time>{' '}
            <strong>{entry.senderName}</strong>
            {entry.channel === 'proximity' && <em> ({t('chat.nearby')})</em>}: {entry.body}
          </li>
        ))}
      </ol>
      {notice && (
        <p role="alert" className="error small">
          {t(`chat.notices.${notice}`)}
        </p>
      )}
      <form onSubmit={send} className="chat-form">
        <select
          value={channel}
          onChange={(event) => {
            const next = event.target.value as ChatChannel;
            setChannel(next);
            if (!nearbyText(text, next)) typing.stop();
          }}
          aria-label={t('chat.channel')}
        >
          <option value="global">{t('chat.global')}</option>
          <option value="proximity">{t('chat.nearby')}</option>
        </select>
        <input
          ref={inputRef}
          value={text}
          maxLength={CHAT_MAX_LENGTH}
          placeholder={t('chat.placeholder')}
          onChange={(event) => {
            setText(event.target.value);
            typing.input(nearbyText(event.target.value, channel));
          }}
          onBlur={() => typing.stop()}
          onKeyDown={(event) => {
            if (event.key === 'Escape') inputRef.current?.blur();
          }}
          autoComplete="off"
        />
      </form>
    </section>
  );
}
